import { css } from "@emotion/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { parseDiffFromFile } from "@pierre/diffs";
import { FileDiff } from "@pierre/diffs/react";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { StreamLanguage } from "@codemirror/language";
import { yaml } from "@codemirror/legacy-modes/mode/yaml";
import { githubDark, githubLight } from "@uiw/codemirror-theme-github";

import { Button, Flex, Heading, Loading, Text } from "@phoenix/components";
import { useTheme } from "@phoenix/contexts";
import { authFetch } from "../../authFetch";
import { prependBasename } from "../../utils/routingUtils";
import type {
  YamlConfigDetail,
  YamlConfigRevisionDetail,
  YamlConfigRevisionMetadata,
} from "./YAMLConfigModals";

// ---------------------------------------------------------------------------
// Pure Helpers (Exported for Unit Tests)
// ---------------------------------------------------------------------------

export function sortRevisions(
  revisions: YamlConfigRevisionMetadata[]
): YamlConfigRevisionMetadata[] {
  return [...revisions].sort((a, b) => b.revision_number - a.revision_number);
}

export function formatShortChecksum(checksum?: string | null): string {
  if (!checksum || checksum.length < 8) return "";
  return `${checksum.slice(0, 4)}...${checksum.slice(-4)}`;
}

export function formatRevisionLabel(revisionNumber: number): string {
  return `v${revisionNumber}`;
}

export function formatRevisionDate(dateStr?: string | null): string {
  if (!dateStr) return "Unknown date";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

export function isCurrentRevision(
  revisionNumber: number,
  currentRevisionNumber?: number | null,
  highestRevisionNumber?: number
): boolean {
  if (currentRevisionNumber !== undefined && currentRevisionNumber !== null) {
    return revisionNumber === currentRevisionNumber;
  }
  if (highestRevisionNumber !== undefined) {
    return revisionNumber === highestRevisionNumber;
  }
  return false;
}

export function buildRevertPayload(
  reason?: string | null,
  targetRevisionNumber?: number
): { change_summary: string } {
  const fallback = targetRevisionNumber
    ? `Reverted to revision v${targetRevisionNumber}`
    : "Reverted configuration";
  const text = (reason && reason.trim()) || fallback;
  return {
    change_summary: text.slice(0, 500),
  };
}

export function getDiffComparisonContents(
  mode: "diff-current" | "diff-previous" | "raw",
  selectedRev: YamlConfigRevisionDetail,
  currentYaml: string,
  previousRev?: YamlConfigRevisionDetail | null
): { oldText: string; newText: string; oldName: string; newName: string } {
  if (mode === "diff-previous" && previousRev) {
    return {
      oldText: previousRev.yaml_content,
      newText: selectedRev.yaml_content,
      oldName: `v${previousRev.revision_number}.yaml`,
      newName: `v${selectedRev.revision_number}.yaml`,
    };
  }
  return {
    oldText: selectedRev.yaml_content,
    newText: currentYaml,
    oldName: `v${selectedRev.revision_number}.yaml (Historical)`,
    newName: "current_active.yaml",
  };
}

// ---------------------------------------------------------------------------
// Emotion CSS Styles
// ---------------------------------------------------------------------------

const modalOverlayStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const modalContainerStyle = css`
  background: #ffffff;
  border-radius: 12px;
  width: 95vw;
  max-width: 1200px;
  height: 86vh;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const modalHeaderStyle = css`
  padding: 14px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8fafc;
`;

const modalBodyStyle = css`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;
  overflow: hidden;
`;

const sidebarStyle = css`
  width: 330px;
  min-width: 290px;
  max-width: 350px;
  border-right: 1px solid #e2e8f0;
  background-color: #f8fafc;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const sidebarListStyle = css`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const revisionCardStyle = (isSelected: boolean, isCurrent: boolean) => css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${isSelected ? "#0f766e" : isCurrent ? "#86efac" : "#e2e8f0"};
  background-color: ${isSelected ? "#f0fdf4" : "#ffffff"};
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  box-shadow: ${isSelected ? "0 1px 3px rgba(15, 118, 110, 0.15)" : "none"};

  &:hover {
    border-color: #0f766e;
    background-color: ${isSelected ? "#f0fdf4" : "#f8fafc"};
  }
`;

const contentAreaStyle = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  overflow: hidden;
`;

const contentToolbarStyle = css`
  padding: 10px 16px;
  border-bottom: 1px solid #e2e8f0;
  background-color: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const contentBodyStyle = css`
  flex: 1;
  min-height: 0;
  overflow: auto;
  background-color: #ffffff;
  position: relative;
`;

const footerStyle = css`
  padding: 12px 20px;
  border-top: 1px solid #e2e8f0;
  background-color: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const bannerStyle = (type: "info" | "warning" | "error" | "success") => {
  const bg =
    type === "info"
      ? "#eff6ff"
      : type === "warning"
        ? "#fffbeb"
        : type === "error"
          ? "#fef2f2"
          : "#f0fdf4";
  const border =
    type === "info"
      ? "#bfdbfe"
      : type === "warning"
        ? "#fde68a"
        : type === "error"
          ? "#fecaca"
          : "#bbf7d0";
  const color =
    type === "info"
      ? "#1e40af"
      : type === "warning"
        ? "#92400e"
        : type === "error"
          ? "#991b1b"
          : "#166534";
  return css`
    padding: 10px 16px;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
    background-color: ${bg};
    border-bottom: 1px solid ${border};
    color: ${color};
  `;
};

// ---------------------------------------------------------------------------
// Component Props
// ---------------------------------------------------------------------------

export interface YAMLRevisionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  configId: string;
  configName: string;
  currentRevisionNumber?: number | null;
  currentYamlContent: string;
  projectId?: string | null;
  onRevertSuccess: (newConfig: YamlConfigDetail) => void;
}

// ---------------------------------------------------------------------------
// Component Implementation
// ---------------------------------------------------------------------------

export function YAMLRevisionHistoryModal({
  isOpen,
  onClose,
  configId,
  configName,
  currentRevisionNumber = null,
  currentYamlContent,
  projectId = null,
  onRevertSuccess,
}: YAMLRevisionHistoryModalProps) {
  const { theme } = useTheme();

  // Revision listing state
  const [revisions, setRevisions] = useState<YamlConfigRevisionMetadata[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Selected revision state
  const [selectedRevNumber, setSelectedRevNumber] = useState<number | null>(null);

  // Revision detail cache { [revision_number]: YamlConfigRevisionDetail }
  const [detailCache, setDetailCache] = useState<Record<number, YamlConfigRevisionDetail>>({});
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // View modes
  const [viewMode, setViewMode] = useState<"diff-current" | "diff-previous" | "raw">("diff-current");
  const [diffStyle, setDiffStyle] = useState<"unified" | "split">("split");

  // Revert workflow state
  const [isConfirmingRevert, setIsConfirmingRevert] = useState(false);
  const [revertReason, setRevertReason] = useState("");
  const [isReverting, setIsReverting] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);

  // 1. Fetch revision metadata list
  const fetchRevisions = useCallback(async () => {
    if (!configId) return;
    setIsLoadingList(true);
    setListError(null);
    try {
      const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
      const url = prependBasename(`/v1/security/targets/yaml/configs/${configId}/revisions${qs}`);
      const res = await authFetch(url);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to load configuration revisions.");
      }
      const data: YamlConfigRevisionMetadata[] = await res.json();
      const sorted = sortRevisions(data);
      setRevisions(sorted);

      // Auto-select initial revision
      if (sorted.length > 0) {
        setSelectedRevNumber((prev) => {
          if (prev !== null && sorted.some((r) => r.revision_number === prev)) {
            return prev;
          }
          // If multiple revisions and current is latest, default to previous revision
          if (sorted.length > 1 && currentRevisionNumber && sorted[0].revision_number === currentRevisionNumber) {
            return sorted[1].revision_number;
          }
          return sorted[0].revision_number;
        });
      } else {
        setSelectedRevNumber(null);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load revisions.";
      setListError(msg);
    } finally {
      setIsLoadingList(false);
    }
  }, [configId, projectId, currentRevisionNumber]);

  // Load revisions on open
  useEffect(() => {
    if (isOpen) {
      setIsConfirmingRevert(false);
      setRevertError(null);
      fetchRevisions();
    }
  }, [isOpen, fetchRevisions]);

  // 2. Fetch revision detail (with full YAML) on demand
  const fetchRevisionDetail = useCallback(
    async (revNum: number) => {
      if (detailCache[revNum]) return detailCache[revNum];

      setIsLoadingDetail(true);
      setDetailError(null);
      try {
        const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
        const url = prependBasename(
          `/v1/security/targets/yaml/configs/${configId}/revisions/${revNum}${qs}`
        );
        const res = await authFetch(url);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Failed to load Revision ${revNum} content.`);
        }
        const data: YamlConfigRevisionDetail = await res.json();
        setDetailCache((prev) => ({ ...prev, [revNum]: data }));
        return data;
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : `Failed to load Revision ${revNum}.`;
        setDetailError(msg);
        return null;
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [configId, projectId, detailCache]
  );

  // Fetch selected revision details when selectedRevNumber changes
  useEffect(() => {
    if (isOpen && selectedRevNumber !== null) {
      fetchRevisionDetail(selectedRevNumber);
      if (viewMode === "diff-previous" && selectedRevNumber > 1) {
        fetchRevisionDetail(selectedRevNumber - 1);
      }
    }
  }, [isOpen, selectedRevNumber, viewMode, fetchRevisionDetail]);

  // Helper computations
  const activeHighestRevision = useMemo(() => {
    return revisions.length > 0 ? revisions[0].revision_number : 1;
  }, [revisions]);

  const selectedRevMetadata = useMemo(() => {
    return revisions.find((r) => r.revision_number === selectedRevNumber) || null;
  }, [revisions, selectedRevNumber]);

  const selectedDetail = useMemo(() => {
    return selectedRevNumber !== null ? detailCache[selectedRevNumber] || null : null;
  }, [selectedRevNumber, detailCache]);

  const previousDetail = useMemo(() => {
    if (selectedRevNumber === null || selectedRevNumber <= 1) return null;
    return detailCache[selectedRevNumber - 1] || null;
  }, [selectedRevNumber, detailCache]);

  const isSelectedRevCurrent = useMemo(() => {
    if (selectedRevNumber === null) return false;
    return isCurrentRevision(selectedRevNumber, currentRevisionNumber, activeHighestRevision);
  }, [selectedRevNumber, currentRevisionNumber, activeHighestRevision]);

  // Diff calculation
  const fileDiff = useMemo(() => {
    if (!selectedDetail || viewMode === "raw") return null;

    if (viewMode === "diff-previous") {
      if (!previousDetail) return null;
      const comparison = getDiffComparisonContents(
        "diff-previous",
        selectedDetail,
        currentYamlContent,
        previousDetail
      );
      return parseDiffFromFile(
        { name: comparison.oldName, contents: comparison.oldText },
        { name: comparison.newName, contents: comparison.newText }
      );
    }

    // Default to diff-current
    const comparison = getDiffComparisonContents(
      "diff-current",
      selectedDetail,
      currentYamlContent
    );
    return parseDiffFromFile(
      { name: comparison.oldName, contents: comparison.oldText },
      { name: comparison.newName, contents: comparison.newText }
    );
  }, [selectedDetail, previousDetail, viewMode, currentYamlContent]);

  // 3. Revert Action
  const handleOpenRevertConfirmation = () => {
    if (selectedRevNumber === null || isSelectedRevCurrent) return;
    setRevertReason(`Reverted to revision v${selectedRevNumber}`);
    setRevertError(null);
    setIsConfirmingRevert(true);
  };

  const handleExecuteRevert = async () => {
    if (selectedRevNumber === null) return;
    setIsReverting(true);
    setRevertError(null);
    try {
      const payload = buildRevertPayload(revertReason, selectedRevNumber);
      const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
      const url = prependBasename(
        `/v1/security/targets/yaml/configs/${configId}/revisions/${selectedRevNumber}/revert${qs}`
      );
      const res = await authFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to revert to Revision ${selectedRevNumber}.`);
      }

      const newConfig: YamlConfigDetail = await res.json();
      setIsConfirmingRevert(false);

      // Refresh list to show the new revision (e.g. v4 CURRENT)
      await fetchRevisions();
      if (newConfig.current_revision_number) {
        setSelectedRevNumber(newConfig.current_revision_number);
      }

      // Propagate revert update to parent page
      onRevertSuccess(newConfig);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Revert failed.";
      setRevertError(msg);
    } finally {
      setIsReverting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div css={modalOverlayStyle}>
      <div css={modalContainerStyle}>
        {/* Header */}
        <div css={modalHeaderStyle}>
          <Flex direction="row" gap="size-100" alignItems="center">
            <Heading level={5} style={{ margin: 0, color: "#0f172a" }}>
              🕒 Revision History — {configName}
            </Heading>
            {currentRevisionNumber && (
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: "9999px",
                  backgroundColor: "#dcfce7",
                  color: "#15803d",
                  border: "1px solid #bbf7d0",
                }}
              >
                Active: v{currentRevisionNumber}
              </span>
            )}
          </Flex>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: "18px",
              color: "#64748b",
            }}
          >
            ✕
          </button>
        </div>

        {/* Body (Split 2 Columns) */}
        <div css={modalBodyStyle}>
          {/* Left Column: Revision Timeline List */}
          <div css={sidebarStyle}>
            <div
              style={{
                padding: "10px 14px",
                borderBottom: "1px solid #e2e8f0",
                fontWeight: 650,
                fontSize: "13px",
                color: "#334155",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>Revisions ({revisions.length})</span>
              {isLoadingList && <Loading size="S" />}
            </div>

            <div css={sidebarListStyle}>
              {listError && (
                <div
                  style={{
                    padding: "8px 10px",
                    borderRadius: "6px",
                    backgroundColor: "#fee2e2",
                    color: "#991b1b",
                    fontSize: "12px",
                  }}
                >
                  {listError}
                  <div style={{ marginTop: "6px" }}>
                    <Button variant="default" size="S" onClick={fetchRevisions}>
                      Retry
                    </Button>
                  </div>
                </div>
              )}

              {revisions.length === 0 && !isLoadingList && !listError && (
                <div style={{ padding: "20px 10px", textAlign: "center", color: "#64748b", fontSize: "12px" }}>
                  No revisions found.
                </div>
              )}

              {revisions.map((rev) => {
                const isSelected = rev.revision_number === selectedRevNumber;
                const isCurrent = isCurrentRevision(
                  rev.revision_number,
                  currentRevisionNumber,
                  activeHighestRevision
                );

                return (
                  <div
                    key={rev.id}
                    css={revisionCardStyle(isSelected, isCurrent)}
                    onClick={() => {
                      setSelectedRevNumber(rev.revision_number);
                      setIsConfirmingRevert(false);
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontWeight: 700, fontSize: "13px", color: isSelected ? "#0f766e" : "#0f172a" }}>
                        v{rev.revision_number}
                      </span>
                      {isCurrent && (
                        <span
                          style={{
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.5px",
                            padding: "1px 6px",
                            borderRadius: "4px",
                            backgroundColor: "#dcfce7",
                            color: "#15803d",
                            border: "1px solid #bbf7d0",
                          }}
                        >
                          CURRENT
                        </span>
                      )}
                    </div>

                    <div style={{ fontSize: "11px", color: "#64748b" }}>
                      {formatRevisionDate(rev.created_at)}
                    </div>

                    <div
                      style={{
                        fontSize: "12px",
                        color: "#1e293b",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={rev.change_summary || undefined}
                    >
                      {rev.change_summary || "No change summary"}
                    </div>

                    {rev.checksum && (
                      <div
                        style={{
                          fontSize: "10px",
                          fontFamily: "ui-monospace, monospace",
                          color: "#64748b",
                        }}
                        title={`Full SHA-256: ${rev.checksum}`}
                      >
                        SHA: {formatShortChecksum(rev.checksum)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column: Diff & Inspection Workspace */}
          <div css={contentAreaStyle}>
            {/* Toolbar */}
            <div css={contentToolbarStyle}>
              {/* Inspection Info */}
              <div style={{ minWidth: 0 }}>
                {selectedRevMetadata ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 650, fontSize: "13px", color: "#0f172a" }}>
                      Revision v{selectedRevMetadata.revision_number}
                    </span>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>
                      ({formatRevisionDate(selectedRevMetadata.created_at)})
                    </span>
                    {isSelectedRevCurrent && (
                      <span
                        style={{
                          fontSize: "11px",
                          backgroundColor: "#dcfce7",
                          color: "#15803d",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          fontWeight: 600,
                        }}
                      >
                        Active Revision
                      </span>
                    )}
                  </div>
                ) : (
                  <span style={{ fontSize: "13px", color: "#64748b" }}>Select a revision to inspect</span>
                )}
              </div>

              {/* View Mode & Style Toggles */}
              <Flex direction="row" gap="size-100" alignItems="center">
                {/* View Mode */}
                <div
                  style={{
                    display: "inline-flex",
                    borderRadius: "6px",
                    padding: "2px",
                    backgroundColor: "#e2e8f0",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setViewMode("diff-current")}
                    style={{
                      border: "none",
                      padding: "4px 10px",
                      borderRadius: "4px",
                      backgroundColor: viewMode === "diff-current" ? "#ffffff" : "transparent",
                      color: viewMode === "diff-current" ? "#0f172a" : "#475569",
                      fontWeight: viewMode === "diff-current" ? 600 : 500,
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  >
                    Diff vs Current
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("diff-previous")}
                    disabled={selectedRevNumber === null || selectedRevNumber <= 1}
                    style={{
                      border: "none",
                      padding: "4px 10px",
                      borderRadius: "4px",
                      backgroundColor: viewMode === "diff-previous" ? "#ffffff" : "transparent",
                      color: viewMode === "diff-previous" ? "#0f172a" : "#475569",
                      fontWeight: viewMode === "diff-previous" ? 600 : 500,
                      fontSize: "12px",
                      cursor: selectedRevNumber !== null && selectedRevNumber > 1 ? "pointer" : "not-allowed",
                      opacity: selectedRevNumber !== null && selectedRevNumber > 1 ? 1 : 0.5,
                    }}
                    title={
                      selectedRevNumber !== null && selectedRevNumber > 1
                        ? `Compare v${selectedRevNumber} with v${selectedRevNumber - 1}`
                        : "Revision 1 is initial configuration (no previous revision)"
                    }
                  >
                    Diff vs Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("raw")}
                    style={{
                      border: "none",
                      padding: "4px 10px",
                      borderRadius: "4px",
                      backgroundColor: viewMode === "raw" ? "#ffffff" : "transparent",
                      color: viewMode === "raw" ? "#0f172a" : "#475569",
                      fontWeight: viewMode === "raw" ? 600 : 500,
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  >
                    Raw YAML
                  </button>
                </div>

                {/* Diff Style (Unified vs Split) */}
                {viewMode !== "raw" && (
                  <div
                    style={{
                      display: "inline-flex",
                      borderRadius: "6px",
                      padding: "2px",
                      backgroundColor: "#e2e8f0",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setDiffStyle("split")}
                      style={{
                        border: "none",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        backgroundColor: diffStyle === "split" ? "#ffffff" : "transparent",
                        color: diffStyle === "split" ? "#0f172a" : "#475569",
                        fontWeight: diffStyle === "split" ? 600 : 500,
                        fontSize: "11px",
                        cursor: "pointer",
                      }}
                    >
                      Split
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiffStyle("unified")}
                      style={{
                        border: "none",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        backgroundColor: diffStyle === "unified" ? "#ffffff" : "transparent",
                        color: diffStyle === "unified" ? "#0f172a" : "#475569",
                        fontWeight: diffStyle === "unified" ? 600 : 500,
                        fontSize: "11px",
                        cursor: "pointer",
                      }}
                    >
                      Unified
                    </button>
                  </div>
                )}
              </Flex>
            </div>

            {/* Informational Banners */}
            {revisions.length === 1 && (
              <div css={bannerStyle("info")}>
                <span>
                  ℹ <strong>Initial Revision:</strong> Revision 1 is the initial configuration. Subsequent saves will create new revisions.
                </span>
              </div>
            )}

            {detailError && (
              <div css={bannerStyle("error")}>
                <span>⚠ {detailError}</span>
                <Button
                  variant="default"
                  size="S"
                  onClick={() => selectedRevNumber !== null && fetchRevisionDetail(selectedRevNumber)}
                >
                  Retry
                </Button>
              </div>
            )}

            {/* Revert Confirmation Banner / Dialog */}
            {isConfirmingRevert && selectedRevNumber !== null && (
              <div
                style={{
                  padding: "14px 20px",
                  backgroundColor: "#fffbeb",
                  borderBottom: "1px solid #fde68a",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Heading level={6} style={{ margin: 0, color: "#92400e" }}>
                    ↺ Revert to Revision v{selectedRevNumber}?
                  </Heading>
                  <span style={{ fontSize: "11px", color: "#b45309", fontWeight: 600 }}>
                    Creates New Revision v{activeHighestRevision + 1}
                  </span>
                </div>

                <Text size="S" color="text-700">
                  This will create a new Revision v{activeHighestRevision + 1} containing the configuration from Revision v{selectedRevNumber}. Existing revisions will remain unchanged in history.
                  Any unsaved edits currently in your editor will be replaced.
                </Text>

                {revertError && (
                  <div
                    style={{
                      padding: "6px 10px",
                      borderRadius: "6px",
                      backgroundColor: "#fee2e2",
                      color: "#991b1b",
                      fontSize: "12px",
                    }}
                  >
                    {revertError}
                  </div>
                )}

                <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    type="text"
                    value={revertReason}
                    onChange={(e) => setRevertReason(e.target.value)}
                    placeholder="Optional change summary (e.g. Reverted to v1 baseline)"
                    maxLength={500}
                    style={{
                      flex: 1,
                      minWidth: "260px",
                      padding: "6px 10px",
                      fontSize: "13px",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      outline: "none",
                    }}
                  />
                  <Flex direction="row" gap="size-100" alignItems="center">
                    <Button
                      variant="default"
                      size="S"
                      onClick={() => setIsConfirmingRevert(false)}
                      isDisabled={isReverting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="S"
                      onClick={handleExecuteRevert}
                      isDisabled={isReverting}
                      style={{ backgroundColor: "#0f766e", color: "#ffffff", fontWeight: 600 }}
                    >
                      {isReverting ? (
                        <Flex direction="row" gap="size-50" alignItems="center">
                          <Loading size="S" />
                          <span>Reverting...</span>
                        </Flex>
                      ) : (
                        `Confirm Revert (Creates v${activeHighestRevision + 1})`
                      )}
                    </Button>
                  </Flex>
                </div>
              </div>
            )}

            {/* Content Body: Diff or Raw YAML */}
            <div css={contentBodyStyle}>
              {isLoadingDetail ? (
                <div style={{ padding: "60px 0" }}>
                  <Flex direction="column" gap="size-100" alignItems="center">
                    <Loading size="M" />
                    <Text size="S">Loading revision content...</Text>
                  </Flex>
                </div>
              ) : viewMode === "raw" ? (
                <CodeMirror
                  value={selectedDetail?.yaml_content || ""}
                  theme={theme === "light" ? githubLight : githubDark}
                  extensions={[StreamLanguage.define(yaml), EditorView.lineWrapping]}
                  readOnly
                  editable={false}
                  height="100%"
                />
              ) : (
                <>
                  {fileDiff && fileDiff.hunks.length === 0 ? (
                    <div style={{ padding: "48px 16px", textAlign: "center", color: "#64748b" }}>
                      <p style={{ margin: 0, fontWeight: 650, fontSize: "14px", color: "#0f172a" }}>
                        ✓ No differences found
                      </p>
                      <p style={{ margin: "6px 0 0 0", fontSize: "12px", color: "#64748b" }}>
                        Revision v{selectedRevNumber} is identical to the comparison target.
                      </p>
                    </div>
                  ) : fileDiff ? (
                    <FileDiff
                      fileDiff={fileDiff}
                      options={{
                        diffStyle,
                        disableFileHeader: true,
                        theme: { light: "pierre-light", dark: "pierre-dark" },
                        themeType: theme === "light" ? "light" : "dark",
                      }}
                    />
                  ) : (
                    <div style={{ padding: "48px 16px", textAlign: "center", color: "#64748b", fontSize: "13px" }}>
                      Select a revision from the left timeline to view differences.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div css={footerStyle}>
              <Button variant="default" size="S" onClick={onClose}>
                Close
              </Button>

              <Flex direction="row" gap="size-100" alignItems="center">
                {selectedRevNumber !== null && (
                  <Button
                    variant="primary"
                    size="S"
                    onClick={handleOpenRevertConfirmation}
                    isDisabled={isSelectedRevCurrent || isConfirmingRevert || isLoadingDetail}
                    title={
                      isSelectedRevCurrent
                        ? "Cannot revert to the current revision (already active)."
                        : `Revert configuration to Revision v${selectedRevNumber}`
                    }
                    style={{
                      backgroundColor: isSelectedRevCurrent ? "#cbd5e1" : "#0f766e",
                      color: "#ffffff",
                      fontWeight: 600,
                    }}
                  >
                    {isSelectedRevCurrent
                      ? `Active Revision (v${selectedRevNumber})`
                      : `↺ Revert to Revision v${selectedRevNumber}`}
                  </Button>
                )}
              </Flex>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
